import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import {
  getLearningGetCourseDetailQueryKey,
  getLearningGetCourseDetailQueryOptions,
  getLearningGetCourseModulesQueryOptions,
  useLearningGetCourseDetail,
  useLearningGetCourseModules,
  useLearningRedeemDiscountCode,
  useLearningStartCheckout,
  useLearningValidateDiscountCode,
} from "@/services/api/learning/learning";
import {
  getGetCourseProgressQueryKey,
  useGetCourseProgress,
} from "@/services/api/learning-progress/learning-progress";
import {
  getListUserEnrollmentsQueryKey,
  useEnrollInCourse,
} from "@/services/api/learning-enrollments/learning-enrollments";
import {
  getGetScormRegistrationStatusForCourseQueryKey,
  useGetScormRegistrationStatusForCourse,
} from "@/services/api/scorm-runtime-api/scorm-runtime-api";
import { useAuthStore } from "@/stores/authStore";
import {
  CourseContentSection,
  CourseHeroSection,
  ShareCourseModal,
} from "@/features/catalog/components";
import { CourseEvaluationModal } from "@/features/learning/components/CourseEvaluationModal";
import { queryClient } from "@/services/queryClient";
import { telemetry } from "@/services/telemetry";
import { API_BASE_URL } from "@/services/config";
import type { CourseModulePlayerDto, CourseProgressResponse } from "@/types/api";
import { formatCoursePrice, toNum } from "@/utils/course-formatters";

// Search params validation schema
const courseDetailSearchSchema = z.object({
  tab: z.enum(["overview", "progress", "content"]).optional().default("overview"),
  // Stripe Checkout return params: success/cancelled drive a refresh + notification, not the grant itself
  // (the grant is server-side via webhook). session_id is echoed back by Stripe on success.
  checkout: z.enum(["success", "cancelled"]).optional(),
  session_id: z.string().optional(),
});

export const Route = createFileRoute("/_marketing/courses_/$courseId")({
  validateSearch: courseDetailSearchSchema,

  // Preload course data in loader for better UX
  loader: async ({ params: { courseId } }) => {
    // Prepare query options
    const courseOptions = getLearningGetCourseDetailQueryOptions(courseId);
    const modulesOptions = getLearningGetCourseModulesQueryOptions(courseId);

    // Parallel data fetching for public course data
    // Note: We don't fetch progress here because it requires authentication
    // and would cause 404 errors for unauthenticated users in the console.
    // Progress is fetched conditionally in the component based on auth state.
    const [course, modules] = await Promise.all([
      // Always fetch course details (public)
      queryClient.ensureQueryData(courseOptions),
      // Always fetch modules (public)
      queryClient.ensureQueryData(modulesOptions),
    ]);

    return {
      course,
      modules,
    };
  },

  component: CourseDetailPage,
});

/**
 * Helper function to find the next module ID
 * Returns the first incomplete module ID or the first module ID as fallback
 */
function findNextModuleId(
  progress: CourseProgressResponse | null | undefined,
  modules: CourseModulePlayerDto[],
): string | undefined {
  // ProgressStatus: 0 = NotStarted, 1 = InProgress, 2 = Completed
  return progress?.modules?.find((m) => m.status !== 2)?.moduleId || modules[0]?.id;
}

type InlineNotification = {
  tone: "error" | "info";
  title: string;
  message: string;
};

/**
 * Extracts a learner-safe message from a rejected mutation. Discount endpoints return RFC7807
 * ProblemDetails (`{ title, detail }`) on the axios error response; fall back to the error message.
 */
function getDiscountErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "response" in error) {
    const data = (error as { response?: { data?: { detail?: string; title?: string } } }).response
      ?.data;
    if (data?.detail) return data.detail;
    if (data?.title) return data.title;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * CourseDetailPage - Course detail and progress tracking page
 *
 * Displays:
 * - Course hero with progress (if enrolled)
 * - Course metadata (length, audience, level)
 * - Course actions (start/continue/share)
 * - Course content
 * - Progress overview (authenticated users only)
 * - Certificate download (when 100% complete)
 */
function CourseDetailPage() {
  const navigate = useNavigate();
  const { courseId } = Route.useParams();
  const search = Route.useSearch();
  const { isAuthenticated } = useAuthStore();

  // State for share modal and notifications
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);
  const [courseUrl, setCourseUrl] = useState("");
  const [inlineNotification, setInlineNotification] = useState<InlineNotification | null>(null);
  const [enrollmentSuccess, setEnrollmentSuccess] = useState(false);

  // Discount-code state (LMS-29). `discountPreview` is non-null only after a server validation succeeds,
  // so a £0.00 preview can never be shown from stale or client-only state.
  const [discountCode, setDiscountCode] = useState("");
  const [discountPreview, setDiscountPreview] = useState<{ compedPriceLabel: string } | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);

  // Get preloaded data from loader
  const loaderData = Route.useLoaderData();

  // Use Suspense queries since data is preloaded in loader
  // These hooks will return the cached data immediately
  const { data: course } = useLearningGetCourseDetail(courseId, {
    query: {
      initialData: loaderData.course,
    },
  });

  const { data: modules = [] } = useLearningGetCourseModules(courseId, {
    query: {
      initialData: loaderData.modules,
    },
  });

  // Fetch progress - only if authenticated
  // This prevents 404 errors in the console for unauthenticated/non-enrolled users
  const { data: progress, error: progressError } = useGetCourseProgress(courseId, {
    query: {
      enabled: isAuthenticated,
      retry: false, // Don't retry if not enrolled yet
      refetchOnWindowFocus: true, // Refresh when tab regains focus
    },
  });

  const { data: scormRegistrationStatus } = useGetScormRegistrationStatusForCourse(courseId, {
    query: {
      enabled: isAuthenticated,
      retry: false,
    },
  });

  // Enrollment mutation
  const enrollMutation = useEnrollInCourse();

  // Checkout mutation (paid courses)
  const checkoutMutation = useLearningStartCheckout();

  // Discount-code mutations (validate previews a comp; redeem grants it directly — no Stripe).
  const validateDiscountMutation = useLearningValidateDiscountCode();
  const redeemDiscountMutation = useLearningRedeemDiscountCode();

  const hasScormRegistration = Boolean(scormRegistrationStatus?.hasRegistration);

  const isEnrolled = (!!progress && !progressError) || hasScormRegistration;

  // Set course URL client-side to avoid SSR hydration issues
  useEffect(() => {
    if (typeof window !== "undefined") {
      setCourseUrl(window.location.href);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [courseId]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (enrollmentSuccess) {
      const timer = setTimeout(() => setEnrollmentSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [enrollmentSuccess]);

  // Handle return from Stripe Checkout. The entitlement grant is server-side (webhook); here we only
  // refresh access-related queries so the unlocked state appears, and surface a notification. We then
  // strip the checkout params from the URL so a refresh/back does not re-trigger this.
  useEffect(() => {
    if (search.checkout !== "success" && search.checkout !== "cancelled") return;

    if (search.checkout === "success") {
      queryClient.invalidateQueries({ queryKey: getLearningGetCourseDetailQueryKey(courseId) });
      queryClient.invalidateQueries({ queryKey: getGetCourseProgressQueryKey(courseId) });
      queryClient.invalidateQueries({ queryKey: getListUserEnrollmentsQueryKey() });
      queryClient.invalidateQueries({
        queryKey: getGetScormRegistrationStatusForCourseQueryKey(courseId),
      });
      setInlineNotification({
        tone: "info",
        title: "Payment received",
        message:
          "We're unlocking your course. This usually only takes a moment — refresh if it doesn't appear.",
      });
      telemetry.trackBusinessEvent("Learning", "CheckoutReturned", {
        courseId,
        outcome: "success",
      });
    } else {
      setInlineNotification({
        tone: "info",
        title: "Checkout cancelled",
        message: "No payment was taken. You can buy the course whenever you're ready.",
      });
      telemetry.trackBusinessEvent("Learning", "CheckoutReturned", {
        courseId,
        outcome: "cancelled",
      });
    }

    // Strip the checkout params from the URL so a refresh/back does not re-trigger this effect.
    navigate({
      to: "/courses/$courseId",
      params: { courseId },
      replace: true,
      search: { tab: search.tab },
    });
  }, [search.checkout, search.tab, courseId, navigate]);

  // Handler for enrollment/navigation
  const handleEnrollOrNavigate = async () => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      // Redirect to login with return URL
      navigate({
        to: "/login",
        search: {
          redirect: window.location.href,
        },
      });
      return;
    }

    // If already enrolled, navigate to player
    if (isEnrolled && progress) {
      // Check for evaluation
      const isCompleted = toNum(progress.percentComplete) >= 100;
      const hasEvaluated = !!progress.evaluationCompletedAt;
      const completionContext = isCompleted ? "completed-review" : "in-progress";

      if (isCompleted && !hasEvaluated) {
        setIsEvaluationModalOpen(true);
        return;
      }

      // Find the next module to navigate to
      const nextModuleId = findNextModuleId(progress, modules);

      navigate({
        to: "/learning/courses/$courseId/player",
        params: { courseId },
        search: nextModuleId ? { moduleId: nextModuleId } : undefined,
      });

      // Track telemetry
      telemetry.trackBusinessEvent("Learning", "ContinueCourseClicked", {
        courseId,
        moduleId: nextModuleId,
        completionContext,
      });
      return;
    }

    if (isEnrolled && !progress) {
      const firstModuleId = modules[0]?.id;
      navigate({
        to: "/learning/courses/$courseId/player",
        params: { courseId },
        search: firstModuleId ? { moduleId: firstModuleId } : undefined,
      });

      telemetry.trackBusinessEvent("Learning", "ContinueCourseClicked", {
        courseId,
        moduleId: firstModuleId,
        completionContext: "in-progress",
      });
      return;
    }

    // Enroll user in course
    try {
      setInlineNotification(null);

      // Track enrollment start
      telemetry.trackBusinessEvent("Learning", "EnrollmentStarted", {
        courseId,
        courseTitle: course?.title,
      });

      await enrollMutation.mutateAsync({
        data: { courseId },
      });

      // Invalidate queries to refresh data (fire-and-forget for responsiveness)
      queryClient.invalidateQueries({
        queryKey: getGetCourseProgressQueryKey(courseId),
      });
      queryClient.invalidateQueries({
        queryKey: getListUserEnrollmentsQueryKey(),
      });

      // Track enrollment success
      telemetry.trackBusinessEvent("Learning", "EnrollmentCompleted", {
        courseId,
        courseTitle: course?.title,
      });

      // Navigate to player immediately with first module
      const firstModuleId = modules[0]?.id;
      navigate({
        to: "/learning/courses/$courseId/player",
        params: { courseId },
        search: firstModuleId ? { moduleId: firstModuleId } : undefined,
      });
    } catch (error) {
      console.error("Enrollment failed:", error);
      setInlineNotification({
        tone: "error",
        title: "Enrollment failed",
        message:
          error instanceof Error ? error.message : "Failed to enroll in course. Please try again.",
      });

      // Track enrollment failure
      telemetry.trackBusinessEvent("Learning", "EnrollmentFailed", {
        courseId,
        courseTitle: course?.title,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  // Handler for starting a paid-course purchase via Stripe Checkout.
  const handleBuy = async () => {
    // Require auth before checkout; send the learner back here after login.
    if (!isAuthenticated) {
      navigate({
        to: "/login",
        search: {
          redirect: window.location.href,
        },
      });
      return;
    }

    try {
      setInlineNotification(null);

      telemetry.trackBusinessEvent("Learning", "CheckoutStarted", {
        courseId,
        courseTitle: course?.title,
      });

      const result = await checkoutMutation.mutateAsync({ courseId });

      if (result?.checkoutUrl) {
        // Hand off to Stripe-hosted Checkout. Entitlement is granted server-side by the webhook.
        window.location.href = result.checkoutUrl;
        return;
      }

      throw new Error("Checkout could not be started.");
    } catch (error) {
      console.error("Checkout failed:", error);
      setInlineNotification({
        tone: "error",
        title: "Checkout failed",
        message:
          error instanceof Error ? error.message : "We couldn't start checkout. Please try again.",
      });

      telemetry.trackBusinessEvent("Learning", "CheckoutFailed", {
        courseId,
        courseTitle: course?.title,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  // Editing the code clears any prior preview/error so a stale £0.00 can't linger after the code changes.
  const handleDiscountCodeChange = (value: string) => {
    setDiscountCode(value);
    setDiscountPreview(null);
    setDiscountError(null);
  };

  // Validate a discount code: the server decides whether it comps the course and returns a £0.00 preview.
  // This never grants access — the learner still confirms via handleConfirmComp.
  const handleApplyDiscount = async () => {
    const trimmed = discountCode.trim();
    if (!trimmed) return; // Empty / whitespace-only codes are a no-op (AC1).

    if (!isAuthenticated) {
      navigate({ to: "/login", search: { redirect: window.location.href } });
      return;
    }

    setDiscountError(null);
    telemetry.trackBusinessEvent("Learning", "DiscountCodeApplied", {
      courseId,
      courseTitle: course?.title,
    });

    try {
      const result = await validateDiscountMutation.mutateAsync({
        courseId,
        data: { code: trimmed },
      });
      setDiscountPreview({
        compedPriceLabel:
          formatCoursePrice(result.discountedPriceAmountPence, result.currency) || "£0",
      });
    } catch (error) {
      setDiscountPreview(null);
      setDiscountError(
        getDiscountErrorMessage(
          error,
          "That discount code could not be applied. Check it and try again.",
        ),
      );
      telemetry.trackBusinessEvent("Learning", "DiscountCodeRejected", {
        courseId,
        courseTitle: course?.title,
      });
    }
  };

  // Confirm a validated comp: redeem the code, which grants the lifetime entitlement directly (no Stripe).
  // We then refresh the access-related queries — mirroring the Stripe-success return — so the unlocked state
  // appears without a reload.
  const handleConfirmComp = async () => {
    if (!isAuthenticated) {
      navigate({ to: "/login", search: { redirect: window.location.href } });
      return;
    }

    const trimmed = discountCode.trim();
    if (!trimmed) return;

    setDiscountError(null);

    try {
      await redeemDiscountMutation.mutateAsync({ courseId, data: { code: trimmed } });

      queryClient.invalidateQueries({ queryKey: getLearningGetCourseDetailQueryKey(courseId) });
      queryClient.invalidateQueries({ queryKey: getGetCourseProgressQueryKey(courseId) });
      queryClient.invalidateQueries({ queryKey: getListUserEnrollmentsQueryKey() });
      queryClient.invalidateQueries({
        queryKey: getGetScormRegistrationStatusForCourseQueryKey(courseId),
      });

      setDiscountPreview(null);
      setDiscountCode("");
      setInlineNotification({
        tone: "info",
        title: "Course unlocked",
        message: "Your discount code was applied and the course is now free for you.",
      });

      telemetry.trackBusinessEvent("Learning", "DiscountCodeRedeemed", {
        courseId,
        courseTitle: course?.title,
      });
    } catch (error) {
      setDiscountPreview(null);
      setDiscountError(
        getDiscountErrorMessage(error, "We couldn't apply that discount code. Please try again."),
      );
      telemetry.trackBusinessEvent("Learning", "DiscountCodeRedeemFailed", {
        courseId,
        courseTitle: course?.title,
      });
    }
  };

  // Handler for sharing
  const handleShare = () => {
    setIsShareModalOpen(true);

    // Track share action
    telemetry.trackBusinessEvent("Marketing", "ShareCourseClicked", {
      courseId,
      courseTitle: course?.title,
    });
  };

  // Handler for certificate download
  // Certificates are generated on-demand when first requested
  const handleCertificateDownload = useCallback(() => {
    // Trigger download by opening the API endpoint
    // The server will generate the certificate if it doesn't exist yet
    // Use full API URL to ensure cross-origin requests work in staging/production
    const downloadUrl = `${API_BASE_URL}/api/learning/certificates/courses/${courseId}/download`;

    // Create a hidden link and click it to trigger the download
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `certificate-${course?.title ?? "course"}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Track the download
    telemetry.trackBusinessEvent("Learning", "CertificateDownloaded", {
      courseId,
      courseTitle: course?.title,
    });
  }, [courseId, course?.title]);

  const notificationIsError = inlineNotification?.tone === "error";

  if (!course) {
    return (
      <main className="bg-arc-base-white py-section-sm md:py-section">
        <div className="mx-auto max-w-3xl px-8 text-center">
          <p className="text-arc-gray-700 text-lg">
            We couldn&apos;t load this course right now. Please refresh and try again.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#fefefe]">
      {/* Enrollment Success Notification */}
      {enrollmentSuccess && (
        <div
          className="bg-arc-success-50 border-arc-success-200 text-arc-success-800 fixed top-24 right-8 z-50 max-w-md rounded-lg border px-6 py-4 shadow-lg sm:right-8"
          role="alert"
          aria-live="polite"
          aria-atomic="true"
        >
          <p className="font-semibold">✓ Successfully enrolled!</p>
          <p className="text-sm">Redirecting to course player...</p>
        </div>
      )}

      {/* Notification Banner */}
      {inlineNotification && (
        <div
          className={`fixed top-24 right-8 z-50 max-w-md rounded-lg border px-6 py-4 shadow-lg sm:right-8 ${
            notificationIsError
              ? "bg-arc-error-50 border-arc-error-200 text-arc-error-800"
              : "bg-arc-primary-50 border-arc-primary-200 text-arc-primary-800"
          }`}
          role="alert"
          aria-live={notificationIsError ? "assertive" : "polite"}
          aria-atomic="true"
        >
          <p className="font-semibold">{inlineNotification.title}</p>
          <p className="text-sm">{inlineNotification.message}</p>
          <button
            type="button"
            className={`mt-2 cursor-pointer rounded text-sm underline hover:no-underline focus-visible:outline-2 focus-visible:outline-offset-2 ${
              notificationIsError
                ? "text-arc-error-900 focus-visible:outline-arc-error-500"
                : "text-arc-primary-900 focus-visible:outline-arc-primary-500"
            }`}
            onClick={() => setInlineNotification(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* The version the learner started was archived, so they start the live version again (LMS-162) */}
      {course.startedVersionArchived && (
        <div className="bg-arc-primary-50 border-arc-primary-200 text-arc-primary-800 border-b px-6 py-4 sm:px-8">
          <div className="mx-auto max-w-5xl" role="status">
            <p className="font-semibold">This course has been updated</p>
            <p className="text-sm">
              The version you started is no longer available, so you&apos;ll start the new version
              from the beginning. Any certificate you&apos;ve already earned is kept.
            </p>
          </div>
        </div>
      )}

      {/* Course Hero Section */}
      <CourseHeroSection
        course={course}
        progress={progress}
        isEnrolled={isEnrolled}
        onEnroll={handleEnrollOrNavigate}
        onShare={handleShare}
        hasScormRegistration={hasScormRegistration}
        onCertificateDownload={handleCertificateDownload}
        onEvaluate={() => setIsEvaluationModalOpen(true)}
        isEnrolling={enrollMutation.isPending}
        onBuy={handleBuy}
        isPurchasing={checkoutMutation.isPending}
        discountCode={discountCode}
        onDiscountCodeChange={handleDiscountCodeChange}
        onApplyDiscount={handleApplyDiscount}
        onConfirmComp={handleConfirmComp}
        comped={Boolean(discountPreview)}
        compedPriceLabel={discountPreview?.compedPriceLabel ?? ""}
        discountError={discountError}
        isValidatingDiscount={validateDiscountMutation.isPending}
        isRedeemingDiscount={redeemDiscountMutation.isPending}
      />

      {/* Course Content Section - Phase 3 */}
      <CourseContentSection
        modules={modules}
        moduleProgress={progress?.modules ?? undefined}
        isEnrolled={isEnrolled}
      />

      {/* Share Course Modal */}
      <ShareCourseModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        courseUrl={courseUrl}
        courseTitle={course.title ?? ""}
      />

      <CourseEvaluationModal
        isOpen={isEvaluationModalOpen}
        onClose={() => setIsEvaluationModalOpen(false)}
        courseId={course.id ?? ""}
        courseTitle={course.title ?? ""}
      />
    </main>
  );
}
